import { sessionRequests } from './sessionRequests';
import type { Provider } from '@supabase/supabase-js';
import { waitForPendingAnonymousClaims } from './anonymousClaim';

const AUTH_EMAIL_ROUTING_TIMEOUT_MS = 8_000;

interface ExtendedImportMeta extends ImportMeta {
  env: ImportMeta['env'] & {
    VITE_AUTH_EMAIL_REDIRECT?: string;
  };
}

function buildEmailRedirect(nextPath?: string): string {
  const baseEnv = (import.meta as ExtendedImportMeta).env?.VITE_AUTH_EMAIL_REDIRECT;
  const base =
    baseEnv && baseEnv.trim().length > 0
      ? baseEnv
      : typeof window !== 'undefined'
        ? window.location.origin
        : '';
  const callbackUrl = new URL('/auth/callback', base);
  if (nextPath) {
    callbackUrl.searchParams.set('next', nextPath);
  }
  return callbackUrl.toString();
}

function resolveEmailRedirectTo(redirectTo?: string): string {
  return (
    redirectTo ||
    (import.meta as ExtendedImportMeta).env?.VITE_AUTH_EMAIL_REDIRECT ||
    (typeof window !== 'undefined' ? window.location.origin : '')
  );
}

function isExactAuthEmailRoutingResponse(value: unknown): value is { exists: boolean } {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const keys = Object.keys(value);
  return (
    keys.length === 1 && keys[0] === 'exists' && typeof Reflect.get(value, 'exists') === 'boolean'
  );
}

function requireAuthenticatedUserId(
  data: { user?: { id?: string } | null },
  method: string
): string {
  const userId = data.user?.id?.trim();
  if (!userId) throw new Error(`${method} did not return an authenticated user`);
  return userId;
}

/**
 * Best-effort protected account routing. Any unavailable or malformed result is neutral (`null`).
 */
export async function resolveAuthEmailExists(
  email: string,
  turnstileToken: string
): Promise<boolean | null> {
  let timeoutId: number | undefined;
  let timedOut = false;
  try {
    const invocation = (async () => {
      const { supabase } = await import('../supabase');
      if (timedOut) return null;
      return supabase.functions.invoke('auth-email-routing', {
        headers: {
          'Cache-Control': 'no-store',
        },
        body: {
          email: email.trim().toLowerCase(),
          turnstileToken,
        },
      });
    })();
    const timeout = new Promise<null>((resolve) => {
      timeoutId = window.setTimeout(() => {
        timedOut = true;
        resolve(null);
      }, AUTH_EMAIL_ROUTING_TIMEOUT_MS);
    });
    const result = await Promise.race([invocation, timeout]);

    if (result === null || result.error || (result.response && !result.response.ok)) return null;
    return isExactAuthEmailRoutingResponse(result.data) ? result.data.exists : null;
  } catch {
    return null;
  } finally {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  }
}

export async function logInWithRedirect(options: {
  provider: Provider;
  redirectTo?: string;
}): Promise<void> {
  const { supabase } = await import('../supabase');
  const redirectTo =
    options.redirectTo || (typeof window !== 'undefined' ? window.location.href : '');

  const { error } = await supabase.auth.signInWithOAuth({
    provider: options.provider,
    options: {
      redirectTo: redirectTo,
      skipBrowserRedirect: false,
    },
  });

  if (error) throw error;
}

export async function logInWithEmail(email: string, password: string): Promise<string> {
  const { supabase } = await import('../supabase');
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return requireAuthenticatedUserId(data, 'Password sign-in');
}

export async function signUpWithEmail(
  email: string,
  password: string,
  options: {
    name?: string;
    nextPath?: string;
    receivesMarketingEmails?: boolean;
    termsVersion?: string;
  } = {}
): Promise<
  { sessionEstablished: false } | { sessionEstablished: true; authenticatedUserId: string }
> {
  const { supabase } = await import('../supabase');
  const redirectTo = buildEmailRedirect(options.nextPath);
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        ...(options.name ? { name: options.name } : {}),
        receives_marketing_emails: options.receivesMarketingEmails ?? false,
        ...(options.termsVersion ? { service_terms_version: options.termsVersion } : {}),
      },
      emailRedirectTo: redirectTo,
    },
  });
  if (error) throw error;
  if (!data.session) return { sessionEstablished: false };
  return {
    sessionEstablished: true,
    authenticatedUserId: requireAuthenticatedUserId(data, 'Email sign-up'),
  };
}

export async function resendVerificationEmail(email: string, nextPath?: string): Promise<void> {
  const { supabase } = await import('../supabase');
  const redirectTo = buildEmailRedirect(nextPath);
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email,
    options: { emailRedirectTo: redirectTo },
  });
  if (error) throw error;
}

export async function sendMagicLink(
  email: string,
  redirectTo?: string,
  receivesMarketingEmails = false,
  termsVersion?: string
): Promise<void> {
  const { supabase } = await import('../supabase');
  const emailRedirectTo = resolveEmailRedirectTo(redirectTo);
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo,
      shouldCreateUser: true,
      data: {
        receives_marketing_emails: receivesMarketingEmails,
        ...(termsVersion ? { service_terms_version: termsVersion } : {}),
      },
    },
  });
  if (error) throw error;
}

/** Completes an email OTP in the browser context that requested it. */
export async function verifyEmailCode(email: string, code: string): Promise<string> {
  const { supabase } = await import('../supabase');
  const { data, error } = await supabase.auth.verifyOtp({
    email: email.trim().toLowerCase(),
    token: code.replace(/\s/gu, ''),
    type: 'email',
  });
  if (error) throw error;
  return requireAuthenticatedUserId(data, 'Email code verification');
}

export async function requestPasswordReset(email: string, redirectTo?: string): Promise<void> {
  const { supabase } = await import('../supabase');
  const emailRedirectTo =
    redirectTo ||
    (typeof window !== 'undefined'
      ? new URL('/auth/reset-password', window.location.origin).toString()
      : resolveEmailRedirectTo());
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: emailRedirectTo,
  });
  if (error) throw error;
}

export async function updatePassword(newPassword: string): Promise<void> {
  const assertOwner = sessionRequests.captureOwner();
  const { supabase } = await import('../supabase');
  assertOwner();
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
}

export async function logOut(): Promise<void> {
  const { supabase } = await import('../supabase');
  await waitForPendingAnonymousClaims();
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}
